import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import { isSameDay, format } from 'date-fns';
import { RANGES } from 'components/DateRangePicker/ranges';

import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';
import TableTopFilters from 'components/TableTopFilters';
import SearchWithFilters from 'components/SearchWithFilters';
import RefundTable from './RefundTable';

import { DATE_FOR_API } from 'utils/dateFormat';
import { usePrevious } from 'hooks';
import { REASON_FILTERS, STATUS_FILTERS } from './helpers';
import schema, { FIELD_NAMES, defaultValues as _defaultValues } from './schema';
import { refundTable as TEXT } from 'locales/en/refund';
import {
  refundAllSelector,
  setParams,
  getRefunds,
  setSearchTerm,
  searchRefunds,
  resetSearch,
  exportRefunds,
} from 'redux/slices/refund';

const Refunds = () => {
  const dispatch = useDispatch();
  const { data, params, pagination, isLoading, searchTerm, searchFilter } =
    useSelector((state) => refundAllSelector(state));

  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchFilter = usePrevious(searchFilter);

  useEffect(() => {
    dispatch(getRefunds());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getRefunds());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchFilter && searchFilter === undefined)
    ) {
      dispatch(getRefunds());
    }
  }, [dispatch, params, prevParams, prevSearchFilter, searchFilter]);

  useEffect(() => {
    if (prevParams !== null && !_isEqual(params, prevParams)) {
      dispatch(getRefunds());
    }
  }, [dispatch, params, prevParams]);

  const onApplyFilter = (data) => {
    const { status, reason, amountCheckbox, ...rest } = data;
    let statusSet = new Set();
    let reasonSet = new Set();

    for (const property in { ...status, ...reason }) {
      if (status[property]) {
        statusSet.add(property);
      }
      if (reason[property]) {
        reasonSet.add(property);
      }
    }

    const statusFilter = Array.from(statusSet).join(',');
    const reasonFilter = Array.from(reasonSet).join(',');

    let params = {
      ...rest,
      status: statusFilter,
      reason: reasonFilter
    };

    dispatch(setParams({ ...params, page: 1 }));
  };

  const onApplyDate = (date) => {
    dispatch(
      setParams({
        from: format(date.startDate, DATE_FOR_API),
        to: format(date.endDate, DATE_FOR_API),
        page: 1,
      })
    );
  };

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
        page: 1,
      })
    );
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
  };

  const range = useMemo(() => {
    let id =
      RANGES.find(
        (item) =>
          isSameDay(new Date(item.range().startDate), new Date(params.from)) &&
          isSameDay(new Date(item.range().endDate), new Date(params.to))
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    return {
      startDate: new Date(params.from),
      endDate: new Date(params.to),
      id,
      key: 'selection',
    };
  }, [params.from, params.to]);

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onSearch = () => {
    dispatch(searchRefunds());
  };

  const onExport = () => dispatch(exportRefunds());

  return (
    <DashboardContainer header={TEXT.header}>
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeInput={onChangeSearchTerm}
        searchTerm={searchTerm}
        onSearch={onSearch}
        placeholder={TEXT.search}
        searchFilter={searchFilter}
      />
      <RefundTable
        topContent={
          <TableTopFilters
            exportHeader={TEXT.export}
            onApplyFilter={onApplyFilter}
            statusFilter={STATUS_FILTERS}
            reasonFilter={REASON_FILTERS}
            defaultValues={_defaultValues(params)}
            schema={schema}
            fieldNames={FIELD_NAMES}
            onApplyDate={onApplyDate}
            range={range}
            onConfirmExport={onExport}
            isExportDisabled={!data.length}
          />
        }
        data={data}
        pagination={pagination}
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      />
    </DashboardContainer>
  );
};

export default Refunds;
